import { useEffect, useState } from 'react'
import { useLocation } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { useFloatingChat } from '../context/FloatingChatContext'
import HomeChat from './HomeChat'
import PersonaAvatar from './PersonaAvatar'

const PERSONA_COLORS: Record<string, string> = {
  forge: '#B25943', mira: '#9C59F5', flux: '#33C7A0', chronicle: '#8C6DBF',
  timbre: '#ED9429', griot: '#4E937A', relay: '#4493D4', sentinel: '#999999',
  cairn: '#7A9E7E', joel: '#59C5FA', glean: '#A89060', wrench: '#8B8B8B',
  ember: '#E06040', lumen: '#F5D623', vector: '#5AA0D0', ledger: '#6B8E70',
  beacon: '#D4A520', anvil: '#7D7D7D',
}

interface QuickSwitchItem {
  type: 'persona' | 'conversation'
  name: string
  displayName: string
  conversationId?: string
  lastMessageAt?: string
}

function QuickSwitchDropdown({
  onSelect,
  onClose,
  currentPersona,
}: {
  onSelect: (item: QuickSwitchItem) => void
  onClose: () => void
  currentPersona: string | null
}) {
  const { data: personas } = useQuery<{ name: string; display_name: string; status?: string }[]>({
    queryKey: ['floating-personas'],
    queryFn: async () => {
      const res = await fetch('/api/personas')
      return res.json()
    },
    staleTime: 30_000,
  })

  const { data: recentConvs } = useQuery<{ id: string; title: string; updated_at: string; participants: { display_name: string }[] }[]>({
    queryKey: ['floating-recent-convs'],
    queryFn: async () => {
      const res = await fetch('/api/conversations?limit=5&sort=recent')
      const data = await res.json()
      return data.slice(0, 5)
    },
    staleTime: 15_000,
  })

  const awakePersonas = (personas || []).filter(p => p.status === 'awake' && p.name !== currentPersona).slice(0, 8)
  const sleepingPersonas = (personas || []).filter(p => p.status !== 'awake' && p.name !== currentPersona).slice(0, 8)

  return (
    <div
      className="absolute bottom-full left-0 right-0 mb-1 rounded-lg shadow-xl z-60 overflow-hidden"
      style={{ background: 'var(--bg-surface)', border: '1px solid var(--border)', maxHeight: 360, overflowY: 'auto' }}
    >
      {/* Recent conversations */}
      {recentConvs && recentConvs.length > 0 && (
        <div className="px-2 pt-2 pb-1">
          <div className="text-xs font-medium px-2 mb-1" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
            RECENT
          </div>
          {recentConvs.map(conv => (
            <button
              key={conv.id}
              onClick={() => {
                // Navigate to conversation instead of opening in bubble
                window.location.href = `/conversation/${conv.id}`
                onClose()
              }}
              className="w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 hover:bg-[var(--bg-elevated)] transition-colors"
              style={{ background: 'none', border: 'none', cursor: 'pointer' }}
            >
              <span className="text-xs" style={{ color: 'var(--text-muted)' }}>💬</span>
              <span className="text-xs truncate flex-1" style={{ color: 'var(--text-primary)' }}>
                {conv.title || 'Conversation'}
              </span>
            </button>
          ))}
        </div>
      )}

      {/* Personas */}
      <div className="px-2 pt-2 pb-2">
        <div className="text-xs font-medium px-2 mb-1" style={{ color: 'var(--text-muted)', fontSize: 10 }}>
          PEOPLE
        </div>
        {awakePersonas.map(p => (
          <button
            key={p.name}
            onClick={() => {
              onSelect({ type: 'persona', name: p.name, displayName: p.display_name })
              onClose()
            }}
            className="w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 hover:bg-[var(--bg-elevated)] transition-colors"
            style={{ background: 'none', border: 'none', cursor: 'pointer' }}
          >
            <PersonaAvatar name={p.name} size={18} />
            <span className="text-xs" style={{ color: 'var(--text-primary)' }}>
              {p.display_name}
            </span>
            <span className="inline-block w-1.5 h-1.5 rounded-full ml-auto" style={{ background: 'var(--success)' }} />
          </button>
        ))}
        {sleepingPersonas.length > 0 && (
          <>
            <div className="text-xs px-2 mt-2 mb-1" style={{ color: 'var(--text-muted)', fontSize: 9 }}>
              sleeping
            </div>
            {sleepingPersonas.map(p => (
              <button
                key={p.name}
                onClick={() => {
                  onSelect({ type: 'persona', name: p.name, displayName: p.display_name })
                  onClose()
                }}
                className="w-full text-left px-2 py-1.5 rounded-md flex items-center gap-2 hover:bg-[var(--bg-elevated)] transition-colors"
                style={{ background: 'none', border: 'none', cursor: 'pointer', opacity: 0.6 }}
              >
                <PersonaAvatar name={p.name} size={18} />
                <span className="text-xs" style={{ color: 'var(--text-secondary)' }}>
                  {p.display_name}
                </span>
              </button>
            ))}
          </>
        )}
      </div>
    </div>
  )
}

export default function FloatingChat() {
  const { chat, closeChat, toggleChat, openChat } = useFloatingChat()
  const { personaName, displayName, isOpen, prefillDraft } = chat
  const color = PERSONA_COLORS[personaName || ''] || '#8b949e'
  const [showDirectory, setShowDirectory] = useState(false)
  const location = useLocation()

  // ESC to close
  useEffect(() => {
    if (!isOpen) return
    const handler = (e: KeyboardEvent) => { if (e.key === 'Escape') closeChat() }
    document.addEventListener('keydown', handler)
    return () => document.removeEventListener('keydown', handler)
  }, [isOpen, closeChat])

  // Hide on conversation pages — the FAB overlaps the Send button.
  // MUST come AFTER all hooks — early return before hooks is a Rules-of-Hooks
  // violation that causes React #300 (hook count mismatch) on route changes.
  const isConversationPage = location.pathname.startsWith('/conversation/')
  if (isConversationPage && !isOpen) return null

  // Always show — either as active chat or as launcher
  if (!personaName) {
    // No active chat — show persistent launcher bubble
    return (
      <>
        <button
          onClick={() => setShowDirectory(!showDirectory)}
          className="fixed bottom-5 right-5 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all z-40"
          style={{
            background: '#484f58',
            transition: 'opacity 0.2s, transform 0.2s',
          }}
          title="Start a chat"
        >
          <span style={{ fontSize: 22 }}>💬</span>
        </button>
        {showDirectory && (
          <div
            className="fixed bottom-20 right-5 rounded-xl shadow-2xl z-50"
            style={{
              width: 300,
              background: 'var(--bg-surface)',
              border: '1px solid var(--border)',
            }}
          >
            <div
              className="px-3 py-2.5 flex items-center justify-between rounded-t-xl"
              style={{ borderBottom: '1px solid var(--border)' }}
            >
              <span className="text-sm font-medium" style={{ color: 'var(--text-primary)' }}>
                Start a chat
              </span>
              <button
                onClick={() => setShowDirectory(false)}
                className="w-6 h-6 flex items-center justify-center rounded hover:bg-[var(--bg-elevated)]"
                style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 14 }}
              >
                ✕
              </button>
            </div>
            <QuickSwitchDropdown
              currentPersona={null}
              onSelect={(item) => {
                if (item.type === 'persona') {
                  openChat(item.name, item.displayName)
                }
                setShowDirectory(false)
              }}
              onClose={() => setShowDirectory(false)}
            />
          </div>
        )}
      </>
    )
  }

  return (
    <>
      {/* Toggle button — always visible when a persona is set */}
      <button
        onClick={toggleChat}
        className="fixed bottom-5 right-5 w-12 h-12 rounded-full flex items-center justify-center shadow-lg transition-all z-40"
        style={{
          background: color,
          opacity: isOpen ? 0 : 1,
          pointerEvents: isOpen ? 'none' : 'auto',
          transform: isOpen ? 'scale(0)' : 'scale(1)',
          transition: 'opacity 0.2s, transform 0.2s',
        }}
        title={`Chat with ${displayName}`}
      >
        <PersonaAvatar name={personaName} size={32} />
      </button>

      {/* Floating chat panel */}
      <div
        className="fixed bottom-5 right-5 flex flex-col rounded-xl shadow-2xl z-50"
        style={{
          width: 384,
          height: 'min(560px, calc(100vh - 80px))',
          background: 'var(--bg-body)',
          border: `1px solid ${color}44`,
          opacity: isOpen ? 1 : 0,
          pointerEvents: isOpen ? 'auto' : 'none',
          transform: isOpen ? 'translateY(0) scale(1)' : 'translateY(16px) scale(0.95)',
          transition: 'opacity 0.2s, transform 0.2s',
        }}
      >
        {/* Panel header */}
        <div
          className="px-3 py-2.5 flex items-center justify-between shrink-0 rounded-t-xl relative"
          style={{ borderBottom: `1px solid ${color}33`, background: `${color}11` }}
        >
          <div className="flex items-center gap-2">
            <PersonaAvatar name={personaName} size={22} />
            <span className="text-sm font-medium" style={{ color }}>
              {displayName}
            </span>
            <button
              onClick={() => setShowDirectory(!showDirectory)}
              className="w-5 h-5 flex items-center justify-center rounded hover:bg-[var(--bg-elevated)] transition-colors"
              style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 10 }}
              title="Switch conversation"
            >
              {showDirectory ? '▲' : '▼'}
            </button>
          </div>
          <button
            onClick={closeChat}
            className="w-6 h-6 flex items-center justify-center rounded hover:bg-[var(--bg-elevated)] transition-colors"
            style={{ color: 'var(--text-muted)', background: 'none', border: 'none', cursor: 'pointer', fontSize: 16 }}
            title="Close chat (Esc)"
          >
            ✕
          </button>

          {/* Quick switch directory */}
          {showDirectory && (
            <QuickSwitchDropdown
              currentPersona={personaName}
              onSelect={(item) => {
                if (item.type === 'persona') {
                  openChat(item.name, item.displayName)
                }
              }}
              onClose={() => setShowDirectory(false)}
            />
          )}
        </div>

        {/* Chat content — only render HomeChat when open to save resources */}
        {isOpen && (
          <div className="flex-1 overflow-hidden">
            <HomeChat
              personaName={personaName}
              displayName={displayName || personaName}
              prefillDraft={prefillDraft || undefined}
              theme={{
                accentColor: color,
                bgGradient: 'var(--bg-body)',
                myBubbleBg: `${color}15`,
                myBubbleBorder: `${color}66`,
                visitorBubbleBg: '#161b22',
                inputBg: '#0d1117',
                inputFocusGlow: `${color}44`,
              }}
            />
          </div>
        )}
      </div>
    </>
  )
}
